import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import Navbar from "../../Navbar/Navbar";
import { FaPlus } from "react-icons/fa";
import Swal from "sweetalert2"; // Import SweetAlert for confirmation and alerts

import baseURL from "../../config";
import {
  Button, Dialog, DialogActions, DialogContent, DialogTitle,
  TextField, Pagination, Box, Paper, Typography, Grid,
  Table, TableBody, TableCell, TableHead, TableRow,
  useMediaQuery, useTheme, IconButton, Stack, Accordion,
  AccordionSummary, AccordionDetails, Avatar, CircularProgress, Switch, ToggleButton, Tooltip
} from '@mui/material';
import { MdCheckCircle, MdCancel } from "react-icons/md";
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import {
  FaBuilding, FaPhone, FaEnvelope, FaMapMarkerAlt,
  FaIdCard, FaGlobe, FaUsers, FaChevronDown, FaFilePdf
} from 'react-icons/fa';
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import { styled } from '@mui/material/styles';
import { motion } from "framer-motion";

const ResponsiveTable = styled(Paper)(({ theme }) => ({
  overflowX: 'auto',
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[3],
  [theme.breakpoints.down('sm')]: {
    boxShadow: 'none',
    backgroundColor: 'transparent'
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 600,
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1),
  },
}));

const VesselOwnerTable = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [allVesselData, setAllVesselData] = useState([]); // Store all data
  const [displayedVesselData, setDisplayedVesselData] = useState([]); // Data to display on current page
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [totalPages, setTotalPages] = useState(1);
  const [selectedVessel, setSelectedVessel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const itemsPerPage = 10;
  const [pdfAvailability, setPdfAvailability] = useState({}); // State to track PDF availability

  useEffect(() => {
    const fetchAllVesselData = async () => {
      const token = localStorage.getItem("token");

      try {
        setLoading(true);
        const response = await axios.get(`${baseURL}/api/vesselOwner/getVesselOwnerALL`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.data && response.data.data) {
          const sortedData = response.data.data.sort((a, b) => {
            const nameA = a.company_name ? a.company_name.toLowerCase() : "";
            const nameB = b.company_name ? b.company_name.toLowerCase() : "";
            return nameA.localeCompare(nameB);
          });

          setAllVesselData(sortedData);
        } else {
          setError("Invalid data format from API");
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        setError(error?.response?.data?.message);
      } finally {
        setLoading(false);
      }
    };

    fetchAllVesselData();
  }, []);


  useEffect(() => {
    if (allVesselData.length > 0) {
      // Sort entire data alphabetically by vesselOwner
      const sortedData = allVesselData.slice().sort((a, b) => {
        const nameA = a.vesselOwner ? a.vesselOwner.toLowerCase() : "";
        const nameB = b.vesselOwner ? b.vesselOwner.toLowerCase() : "";
        return nameA.localeCompare(nameB);
      });

      // Filter data based on search query
      const filteredData = searchQuery
        ? sortedData.filter(vessel =>
          vessel.company_name &&
          vessel.company_name.toLowerCase().includes(searchQuery.toLowerCase())
        )
        : sortedData;

      // Calculate total pages
      const calculatedTotalPages = Math.ceil(filteredData.length / itemsPerPage);
      setTotalPages(calculatedTotalPages);

      // Handle case where current page is greater than new total pages
      const safePage = currentPage > calculatedTotalPages ? 1 : currentPage;
      if (currentPage > calculatedTotalPages) {
        setCurrentPage(safePage);
      }

      // Get data for current page (apply pagination after sorting)
      const startIndex = (safePage - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      setDisplayedVesselData(filteredData.slice(startIndex, endIndex));
    }
  }, [allVesselData, searchQuery, currentPage, itemsPerPage]);

  const VesselOwnerContract = async (vesselOwnerId) => {
    const token = localStorage.getItem("token");
    try {
      const response = await axios.get(`${baseURL}/api/getVesselOwnerContractById/${vesselOwnerId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.contracts?.length > 0) {
        const contract = response.data.contracts[0]; // First contract object

        if (contract.vesselOwners?.length > 0) {
          const vesselOwner = contract.vesselOwners[0]; // First vessel owner object

          if (vesselOwner.documentTypes && vesselOwner.documentTypes.path) {
            const pdfPath = vesselOwner.documentTypes.path;

            const pdfUrl = `${baseURL}/${pdfPath.replace(/\\/g, "/")}`; // Convert Windows-style paths
            window.open(pdfUrl, "_blank"); // Open PDF in a new tab

            // Update state to mark PDF as available
            setPdfAvailability((prev) => ({ ...prev, [vesselOwnerId]: true }));
          } else {
            alert("No PDF available.");
            setPdfAvailability((prev) => ({ ...prev, [vesselOwnerId]: false }));
          }
        } else {
          alert("No vessel owner data available.");
          setPdfAvailability((prev) => ({ ...prev, [vesselOwnerId]: false }));
        }
      } else {
        alert("No contract data found.");
        setPdfAvailability((prev) => ({ ...prev, [vesselOwnerId]: false }));
      }
    } catch (err) {
      console.error("Error fetching vessel owner contract:", err);
      alert("No PDF Available");
      setPdfAvailability((prev) => ({ ...prev, [vesselOwnerId]: false }));
    }
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1); // Reset to first page when search changes
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const handleViewDetails = (vessel) => {
    setSelectedVessel(vessel);
  };

  const handleCloseDetails = () => {
    setSelectedVessel(null);
  };


  const handleToggleVesselStatus = async (vesselId, newStatus) => {
    // Confirm before deactivating
    if (!newStatus) {
      const result = await Swal.fire({
        title: "Are you sure?",
        text: "Do you really want to deactivate this vessel owner?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "Yes, deactivate it!",
      });

      if (!result.isConfirmed) {
        return; // Stop if the user cancels
      }
    }
    const token = localStorage.getItem("token");
    try {
      const apiEndpoint = newStatus
        ? `${baseURL}/api/vesselOwner/approve/${vesselId}`
        : `${baseURL}/api/vesselOwner/unapprove/${vesselId}`;

      await axios.post(apiEndpoint, { approved: newStatus },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // Show success alert
      Swal.fire({
        icon: "success",
        title: "Success",
        text: `Vessel owner has been ${newStatus ? "activated" : "deactivated"} successfully.`,
        timer: 2000,
        showConfirmButton: false,
      });

      // Refresh the page to reflect the changes
      setTimeout(() => {
        window.location.reload();
      }, 2000);

    } catch (error) {
      console.error("Error updating vessel status:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to update vessel status. Please try again.",
      });
    }
  };


  return (
    <>
      <Navbar />
      {/* Error message */}
      {error && (
        <Box p={4} textAlign="center" bgcolor="#fdecea" color="#b71c1c">
          <Typography color="error" variant="h6">
            {error}
          </Typography>
        </Box>
      )}

      {/* Loading indicator */}
      {loading && !error && (
        <Box p={4} textAlign="center">
          <CircularProgress />
        </Box>
      )}

      <Box px={isMobile ? 2 : 4} mt={4}>
        <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} mb={4} flexWrap="wrap">

          {/* Search Bar */}
          <TextField
            label="Search by Company Name"
            variant="outlined"
            size="small"
            value={searchQuery || ""}
            onChange={handleSearchChange}
            sx={{
              maxWidth: 300,
              backgroundColor: "#f8f9fa",
              borderRadius: "8px",
              flexShrink: 0, // Prevents it from shrinking too much
            }}
          />

          {/* <Box>
            <Link to="/AssignedCrewCvVesselowner" style={{ textDecoration: "none" }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<FaPlus />}
                sx={{
                  borderRadius: "8px",
                  py: 1,
                  textTransform: "none",
                  fontSize: "0.875rem",
                  whiteSpace: "nowrap",
                  backgroundColor: "#C30B12",
                }}
              >
                Assign CVs to Vessel Owner
              </Button>
            </Link>
          </Box> */}


          {/* Vessel Owner Information (Centered) */}
          <Typography
            variant="h5"
            sx={{
              display: "flex",
              alignItems: "center",
              flexGrow: 1, // Allows centering between search & button
              justifyContent: "center", // Centers the text
              color: "#C30B12",
              fontSize: '1.5rem',
            }}
          >
            <PersonOutlineIcon sx={{ mr: 1.5, fontSize: "2rem", }} />
            Vessel Owner Information
          </Typography>



          <Box>
            <Link to="/VesselOwnerContract" style={{ textDecoration: "none" }}>
              <Button
                variant="contained"
                startIcon={<FaPlus />}
                sx={{
                  borderRadius: "8px",
                  py: 1,
                  textTransform: "none",
                  fontSize: "0.875rem",
                  whiteSpace: "nowrap",
                  backgroundColor: "#C30B12",
                }}
              >
                Contract Document
              </Button>
            </Link>
          </Box>

          {/* Add Vessel Owner Button */}
          <Box>
            <Link to="/vesselownerform" style={{ textDecoration: "none" }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<FaPlus />}
                sx={{
                  borderRadius: "8px",
                  py: 1,
                  textTransform: "none",
                  fontSize: "0.875rem",
                  whiteSpace: "nowrap",
                  backgroundColor: "#C30B12",
                }}
              >
                Add Vessel Owner
              </Button>
            </Link>
          </Box>

        </Box>


        <ResponsiveTable>
          {isMobile ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {displayedVesselData.length > 0 ? (
                displayedVesselData.map((vessel, index) => (
                  <Paper key={index} sx={{ p: 2, borderRadius: '8px', boxShadow: 3 }}>
                    <Stack spacing={1.5}>
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="subtitle2" color="textSecondary">
                          #{((currentPage - 1) * itemsPerPage) + index + 1}
                        </Typography>
                        <Avatar sx={{
                          bgcolor: theme.palette.primary.main,
                          width: 32, height: 32,
                          fontSize: '0.875rem'
                        }}>
                          {vessel.company_name?.charAt(0) || '?'}
                        </Avatar>
                      </Box>
                      <Typography variant="subtitle1" fontWeight={600}>
                        {vessel.company_name || 'No Company Name'}
                      </Typography>
                      <Typography variant="body2">
                        <FaPhone style={{ marginRight: 8 }} />
                        {vessel.phoneno || 'N/A'}
                      </Typography>
                      <Typography variant="body2">
                        <FaUsers style={{ marginRight: 8 }} />
                        {vessel.contactperson || 'N/A'}
                      </Typography>
                      <Stack direction="row" spacing={1} mt={1}>
                        <Button
                          variant="outlined"
                          color="primary"
                          size="small"
                          onClick={() => handleViewDetails(vessel)}
                          sx={{ borderRadius: '6px', textTransform: 'none' }}
                        >
                          View
                        </Button>
                        {vessel._id && (
                          <IconButton
                            component={Link}
                            to={`/VesselTableByVesselOwnerId/${vessel._id}`}
                            size="small"
                            sx={{
                              border: `1px solid ${theme.palette.primary.main}`,
                              borderRadius: '6px'
                            }}
                          >
                            <FaPlus size={14} />
                          </IconButton>
                        )}
                      </Stack>
                    </Stack>
                  </Paper>
                ))
              ) : (
                <Box p={4} textAlign="center">
                  <Typography color="textSecondary">No vessel owners found</Typography>
                </Box>
              )}
            </Box>
          ) : (
            <Table sx={{ minWidth: 650 }}>
              <TableHead sx={{ bgcolor: "#C30B12" }}>
                <TableRow>
                  <StyledTableCell sx={{ color: 'white', width: '80px' }}>Sr. No.</StyledTableCell>
                  <StyledTableCell sx={{ color: 'white' }}>Company Information</StyledTableCell>
                  <StyledTableCell sx={{ color: 'white', width: '200px' }}>Contract</StyledTableCell>
                  <StyledTableCell sx={{ color: 'white', width: '300px' }}>Actions</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayedVesselData.length > 0 ? (
                  displayedVesselData.map((vessel, index) => (
                    <TableRow key={index} hover>
                      <TableCell>{((currentPage - 1) * itemsPerPage) + index + 1}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ bgcolor: "#C30B12" }}>
                            {vessel.company_name?.charAt(0) || '?'}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle1" fontWeight={600}>
                              {vessel.company_name || 'No Company Name'}
                            </Typography>
                            <Typography variant="body2">
                              <FaPhone style={{ marginRight: 8 }} />
                              {vessel.phoneno || 'N/A'}
                            </Typography>
                            <Typography variant="body2">
                              <FaUsers style={{ marginRight: 8 }} />
                              {vessel.contactperson || 'N/A'}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Tooltip title="Vessel Owner Contract" arrow>
                          <IconButton onClick={() => VesselOwnerContract(vessel._id)}>
                            <PictureAsPdfIcon
                              sx={{ color: pdfAvailability[vessel._id] ? "green" : "red" }} // Dynamic color
                            />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          <Button
                            variant="contained"

                            size="small"
                            onClick={() => handleViewDetails(vessel)}
                            sx={{ borderRadius: '6px', textTransform: 'none', backgroundColor: "#C30B12" }}
                          >
                            View Details
                          </Button>
                          {vessel._id && (
                            <Tooltip title="Add Vessel" arrow>
                              <IconButton
                                component={Link}
                                to={`/VesselTableByVesselOwnerId/${vessel._id}`}
                                size="small"
                                sx={{
                                  border: `1px solid ${theme.palette.primary.main}`,
                                  borderRadius: '6px'
                                }}
                              >
                                <FaPlus size={14} />
                              </IconButton>
                            </Tooltip>
                          )}

                          {/* Toggle Active/Deactive */}
                          <motion.div
                            whileTap={{ scale: 0.9 }}
                            whileHover={{ scale: 1.05 }}
                            transition={{ duration: 0.2 }}
                          >
                            <ToggleButton
                              value="check"
                              selected={vessel.approved}
                              onChange={() => handleToggleVesselStatus(vessel._id, !vessel.approved)}
                              sx={{
                                backgroundColor: vessel.approved ? "green" : "red",
                                color: "white",
                                borderRadius: "20px",
                                fontWeight: "bold",
                                "&:hover": {
                                  backgroundColor: vessel.approved ? "#0b8a0b" : "#b30000",
                                },
                                transition: "background-color 0.3s ease-in-out",
                              }}
                            >
                              {vessel.approved ? <MdCheckCircle /> : <MdCancel />}
                              {vessel.approved ? " Actived" : " Deactived"}
                            </ToggleButton>

                          </motion.div>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      <Typography color="textSecondary">No vessel owners found</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </ResponsiveTable>

        {totalPages > 1 && (
          <Box mt={4} display="flex" justifyContent="center">
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              variant="outlined"
              shape="rounded"
              size={isMobile ? 'small' : 'medium'}
              sx={{
                '& .MuiPaginationItem-root': {
                  borderRadius: '6px',
                }
              }}
            />
          </Box>
        )}
      </Box>

      {/* Details Dialog */}
      {selectedVessel && (
        <Dialog
          open={!!selectedVessel}
          onClose={handleCloseDetails}
          fullWidth
          maxWidth="md"
          fullScreen={isMobile}
        >
          <DialogTitle sx={{
            bgcolor: "#C30B12",
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            py: 2
          }}>
            <FaBuilding style={{ marginRight: 12 }} />
            {selectedVessel.company_name || 'Vessel Owner Details'}
          </DialogTitle>
          <DialogContent sx={{ p: isMobile ? 1 : 3 }}>
            <Box sx={{ mt: 2 }}>
              <Accordion defaultExpanded={!isMobile}>
                <AccordionSummary expandIcon={<FaChevronDown />}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    <FaBuilding style={{ marginRight: 8 }} />
                    Company Details
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <DetailItem icon={<FaUsers />} title="Short Name" value={selectedVessel.company_shortname} />
                      <DetailItem icon={<FaPhone />} title="Phone" value={selectedVessel.phoneno} />
                      <DetailItem icon={<FaUsers />} title="Contact Person" value={selectedVessel.contactperson} />
                      <DetailItem icon={<FaEnvelope />} title="Email" value={selectedVessel.email} />
                      <DetailItem icon={<FaMapMarkerAlt />} title="Address" value={selectedVessel.address} />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <DetailItem icon={<FaIdCard />} title="PAN" value={selectedVessel.companyPAN} />
                      <DetailItem icon={<FaIdCard />} title="GST" value={selectedVessel.companyGST} />
                      <DetailItem icon={<FaGlobe />} title="Country" value={selectedVessel.country} />
                      <DetailItem icon={<FaFilePdf />} title="Documents"
                        value={
                          selectedVessel.companyDocument?.filePath ? (
                            <a
                              href={`${baseURL}/${selectedVessel.companyDocument.filePath}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: theme.palette.primary.main }}
                            >
                              View Document
                            </a>
                          ) : 'No documents available'
                        }
                      />
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>

              <Accordion>
                <AccordionSummary expandIcon={<FaChevronDown />}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    <FaUsers style={{ marginRight: 8 }} />
                    Crewing Details
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <DetailItem icon={<FaPhone />} title="Primary name" value={selectedVessel.crewing_department11 || 'N/A'} />
                      <DetailItem
                        icon={<FaPhone />}
                        title="Primary Contact"
                        value={selectedVessel.phonecrewing_department1 || 'N/A'}
                      />
                      <DetailItem
                        icon={<FaEnvelope />}
                        title="Primary Email"
                        value={selectedVessel.crewemail1 || 'N/A'}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <DetailItem
                        icon={<FaUsers />}
                        title="Secondary name"
                        value={selectedVessel.crewing_department22 || 'N/A'}
                      />
                      <DetailItem
                        icon={<FaPhone />}
                        title="Secondary Contact"
                        value={selectedVessel.phonecrewing_department2 || 'N/A'}
                      />
                      <DetailItem
                        icon={<FaEnvelope />}
                        title="Secondary Email"
                        value={selectedVessel.crewemail2 || 'N/A'}
                      />
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>

              <Accordion>
                <AccordionSummary expandIcon={<FaChevronDown />}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    <FaIdCard style={{ marginRight: 8 }} />
                    Accounting Details
                  </Typography>
                </AccordionSummary>
                {selectedVessel && (
                  <AccordionDetails>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <DetailItem
                          icon={<FaUsers />}
                          title="Primary name"
                          value={selectedVessel.accounts_department11 || 'N/A'}
                        />
                        <DetailItem
                          icon={<FaPhone />}
                          title="Primary Contact"
                          value={selectedVessel?.phoneaccounts_department1 || 'N/A'}
                        />
                        <DetailItem
                          icon={<FaEnvelope />}
                          title="Primary Email"
                          value={selectedVessel?.accountsEmail || 'N/A'}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <DetailItem
                          icon={<FaUsers />}
                          title="Secondary name"
                          value={selectedVessel?.anotherAccountsPersonName || 'N/A'}
                        />
                        <DetailItem
                          icon={<FaPhone />}
                          title="Secondary Contact"
                          value={selectedVessel?.anotherAccountsPhoneNumber || 'N/A'}
                        />
                        <DetailItem
                          icon={<FaEnvelope />}
                          title="Secondary Email"
                          value={selectedVessel?.anotherAccountsEmail || 'N/A'}
                        />
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                )}
              </Accordion>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button
              onClick={handleCloseDetails}
              variant="outlined"
              sx={{ borderRadius: '6px', textTransform: 'none' }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

const DetailItem = ({ icon, title, value }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
    <Box sx={{
      width: 32,
      height: 32,
      borderRadius: '6px',
      bgcolor: 'action.hover',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      mr: 2
    }}>
      {icon}
    </Box>
    <Box>
      <Typography variant="caption" color="textSecondary">{title}</Typography>
      <Typography variant="body2">{value || 'N/A'}</Typography>
    </Box>
  </Box>
);

export default VesselOwnerTable;